import React, { useEffect, useReducer, useRef, useState, useMemo} from 'react';
import {DownloadInfoItem} from '../../../../types/analytics';
import {analyticsUserInput} from '../../../../utils/analyticsUserInput';
import {IAnalytcisUserInput} from '../../../../types/IAnalyticsUserInput';
import {useIsDwf, useIsTnw, useSignalR} from '../../../../utils/hooks';
import {TabKey} from '../../../../utils/common';
import {useMarkerStore} from "../../../../store/markerStore";
import useLegendStore from "../../../../store/legendStore";
import useHomeStore from "../../../../store/homeStore";
import useAuthStore from "../../../../store/authStore";
import useAnalyticsStore from "../../../../store/analyticsStore";
import {useBasicAnalyticsData} from "../hooks/useBasicAnalyticsData";
import {useTimeSeriesPlot} from "../hooks/useTimeSeriesPlot";
import {useRosePlot} from "../hooks/useRosePlot";
import {useScatterPlot} from "../hooks/useScatterPlot";
import {useAdvancedAnalytics} from "../hooks/useAdvanceAnalytics";
import {useScatterTable} from "../hooks/useScatterTable";
import {useAnalyticsNavigation} from "../hooks/useAnalyticsNavigation";
import {UserGroupPermissions} from "../../../../types/userGroupPermissions";
import {useAnalyticsCommonInput} from "../hooks/useAnalyticsCommonInput";
import ListNavigation from "./ListNavigation";
import {AnalyticsSpinner} from "./AnalyticsSpinner";
import AnalyticsLayout from "./Layout/AnalyticsLayout";
import {Select} from "antd";
import { getDateRange } from '../../../../utils/dateRange'
import ScatterPlotBox from "./Wrapper/ScatterPlotBox";
import TSPlotBox from "./Wrapper/TSPlotBox";
import RosePlotBox from "./Wrapper/RosePlotBox";
import ScatterTableBox from "./Wrapper/ScatterTableBox";
import StatisticsBox from "./Wrapper/StatisticsBox";
import TidalLevelBox from "./Wrapper/TidalLevelBox";
import ExceedanceBox from "./Wrapper/ExceedanceBox";
import HistogramBox from "./Wrapper/HistogramBox";
import PersistenceBox from "./Wrapper/PersistenceBox";
import ExtremeValuesBox from "./Wrapper/ExtremeValuesBox";
import {IMarker} from "../../../../types/marker";
import {DatasetChangeWarningModal} from "../../ResultPanel/component/Modal/DatasetWarning/DatasetChangeWarningModal";
import { useIsFwe } from '../../../../hooks/useIsFwe';

export default function AnalyticsPanel(props: {
  downloadItems: DownloadInfoItem[];
  coordinates: number[];
  previewDatasetId: string;
  userGroupPermissions: UserGroupPermissions;
  pointProps: string;
  pointName: string;
  gridCellSize: string;
  waterDepth: string;
  point?: IMarker
  category: string | string[];
  onMeshControl: (value: string) => any;
  setDatasetIsOfflineWarningVisible: React.Dispatch<React.SetStateAction<boolean>>;
  onChangeActiveKey: (item: any, key: TabKey) => void
}) {
  const { point } = props

  const showErrorFromSignalR = useRef<boolean>(false)

  const [isForAllAnalytics, setIsForAllAnalytics] = useState(false)
  const [analyticsDateTime, setAnalyticsDateTime] = useState<any[]>([])

  const [userInput, updateUserInput] = useReducer(
    (state: IAnalytcisUserInput, update: Partial<IAnalytcisUserInput>) => ({
      ...state,
      ...update,
    }),
    analyticsUserInput
  );

  const enrichedMetadata = useAnalyticsStore(state => state.enrichedMetadata)
  const user = useAuthStore(state => state.user);
  const isAuthenticated = useAuthStore(state => state.isAuthenticated);
  const setShowLogin = useHomeStore(state => state.setShowLogin);
  const timeStepInterval = useHomeStore(state => state.timeStepInterval)
  const showMesh = useLegendStore(state => state.showMesh);
  const markers = useMarkerStore(state => state.markers);
  const updateMarker = useMarkerStore(state => state.updateMarker);
  const setActiveDatasetKey = useMarkerStore(state => state.setActiveDatasetKey)
  const isOfflineDataset = point?.isDatasetOffline?.[props.category as string] ?? true

  const isTnw = useIsTnw(props.previewDatasetId) && user.authenticated
  const isDwf = useIsDwf(props.previewDatasetId) && user.authenticated
  const isFwe = useIsFwe(props.previewDatasetId)

  const {
    timeSeriesPreview,
    timeSeriesPreviewError,
    loadingInitialAnalytics,
    analyticsDefinition,
    defaultRosePlotDirection,
    rosePlotPreview,
    rosePlotPreviewError,
  } = useBasicAnalyticsData({
    previewDatasetId: props.previewDatasetId,
    coordinates: props.coordinates,
    pointName: props.pointName,
    gridCellSize: props.gridCellSize,
    waterDepth: props.waterDepth,
    point: props.point,
    setDatasetIsOfflineWarningVisible: props.setDatasetIsOfflineWarningVisible,
    isOffline: isOfflineDataset,
    isFwe: isFwe,
    fwePointName: props.point?.fwePointName
  })

  const tsPlot = useTimeSeriesPlot({
    coordinates: props.coordinates,
    timeStepInterval,
    pointLabel: props.point?.label || '',
    isFwe: isFwe,
    fwePointName: props.point?.fwePointName
  })

  const rosePlot = useRosePlot({
    coordinates: props.coordinates,
    timeStepInterval,
    pointLabel: props.point?.label || '',
    isFwe: isFwe,
    fwePointName: props.point?.fwePointName
  })

  const scatterPlot = useScatterPlot({
    coordinates: props.coordinates,
    timeStepInterval,
    pointLabel: props.point?.label || '',
    isFwe: isFwe,
    fwePointName: props.point?.fwePointName
  })

  const advancedAnalytics = useAdvancedAnalytics(props.coordinates, isFwe, props.point?.fwePointName)

  const scatterTable = useScatterTable(props.coordinates)

  const commonInputAnalytics = useAnalyticsCommonInput({
    downloadItems: props.downloadItems,
    category: props.category,
    coordinates: props.coordinates,
    previewDatasetId: props.previewDatasetId,
    point: props.point,
    onChangeActiveKey: props.onChangeActiveKey,
    enrichedMetadata,
    showMesh,
    onMeshControl: props.onMeshControl,
    updateMarker,
    markers,
    setActiveDatasetKey
  })

  const analyticsNavigation = useAnalyticsNavigation(
    analyticsDefinition,
    props.userGroupPermissions,
    user,
    isTnw,
    isAuthenticated,
    props.coordinates,
    props.previewDatasetId,
    setShowLogin
  )

  const { messageReceived, receivedMessageText, resetMessageState } = useSignalR("AnalyticsReceiveMessage", "JobResult");

  useEffect(() => {
    if (messageReceived) {
      const splitMessage = receivedMessageText.split(':');
      let hubMessage: string;

      if (splitMessage.length > 1) {
        hubMessage = splitMessage[splitMessage.length - 1];
      } else {
        hubMessage = receivedMessageText;
      }

      if (hubMessage && hubMessage.toLowerCase() !== 'success') {
        advancedAnalytics.setErrorMessage(advancedAnalytics.advancedAnalyticType, hubMessage);
      }
      showErrorFromSignalR.current = true
      resetMessageState();
    } else {
      showErrorFromSignalR.current = false
    }
  }, [messageReceived])

  useEffect(() => {
    advancedAnalytics.resetAdvancedAnalytics()
    scatterTable.resetScatterTable()
    tsPlot.resetTSPlot()
    rosePlot.resetRosePlot()
    scatterPlot.resetScatterPlot()
    updateUserInput({ ...analyticsUserInput });
  }, [props.previewDatasetId]);

  if (loadingInitialAnalytics) {
    return <AnalyticsSpinner />
  }

  const isUpdating = advancedAnalytics.isUpdating || tsPlot.loadingUpdateTS || rosePlot.loadingRosePlot || scatterPlot.loadingScatterPlot || scatterTable.isUpdatingScatterTable

  return (
    <>
    <DatasetChangeWarningModal
      visible={commonInputAnalytics.isChangeDatasetWarningVisible}
      onOk={commonInputAnalytics.handleChangeDatasetWarningOk}
      onCancel={commonInputAnalytics.handleChangeDatasetWarningCancel}
    />
    <AnalyticsLayout
      globalContent={
        <div style={{ padding: '8px' }}>
          <div style={{
            fontSize: '14px',
            fontWeight: 500,
            marginBottom: '6px',
            display: 'flex',
            alignItems: 'center',
          }}>
            <span>Point</span>
          </div>
          <Select
            style={{ width: '100%', height: '30px' }}
            placeholder="Select point"
            onChange={commonInputAnalytics.handleChangePoint}
            options={commonInputAnalytics.pointOptions}
            value={point}
            disabled={loadingInitialAnalytics || isUpdating}
            size="small"
          />
        </div>
      }
      navigationContent={
        <ListNavigation
          availableAnalytics={analyticsNavigation.availableAnalytics}
          activePlotAnalytics={analyticsNavigation.activePlotAnalytics[0]}
          handleAnalyticsTypeChanged={analyticsNavigation.handleAnalyticsTypeChanged}
          isLoading={isUpdating}
        />
      }
    >
      <TSPlotBox
        timeSeriesPreview={timeSeriesPreview}
        activePlotAnalytics={analyticsNavigation.activePlotAnalytics[0]}
        tsPreviewError={timeSeriesPreviewError}
        onDatasetChange={commonInputAnalytics.onDatasetChange}
        previewDatasetId={props.previewDatasetId}
        isForAllAnalytics={isForAllAnalytics}
        setIsForAllAnalytics={setIsForAllAnalytics}
        globalDateRange={analyticsDateTime}
        setGlobalDateRange={setAnalyticsDateTime}
        previewDatasetTimeRange={getDateRange(props.previewDatasetId, props.userGroupPermissions, isTnw, isDwf)}
        categoryDataset={commonInputAnalytics.categoryDataset}
        onUpdateTS={tsPlot.onUpdateTS}
        updatedTSData={tsPlot.updatedTSData}
        pointProps={props.pointProps}
        pointName={props.pointName}
        userInput={userInput}
        updateUserInput={updateUserInput}
        errorUpdate={tsPlot.errorTs}
        loadingButtonUpdate={tsPlot.loadingUpdateTS}
        onChangeActiveKey={commonInputAnalytics.handleChangePoint}
        pointOption={commonInputAnalytics.pointOptions}
        isOffline={isOfflineDataset}
      />
      <RosePlotBox
        rosePlotPreview={rosePlotPreview}
        activePlotAnalytics={analyticsNavigation.activePlotAnalytics[0]}
        rosePlotPreviewError={rosePlotPreviewError}
        previewDatasetId={props.previewDatasetId}
        userGroupPermissionDateRange={getDateRange(props.previewDatasetId, props.userGroupPermissions, isTnw, isDwf)}
        isForAllAnalytics={isForAllAnalytics}
        setIsForAllAnalytics={setIsForAllAnalytics}
        globalDateRange={analyticsDateTime}
        setGlobalDateRange={setAnalyticsDateTime}
        onDatasetChange={commonInputAnalytics.onDatasetChange}
        categoryDataset={commonInputAnalytics.categoryDataset}
        defaultRosePlotDirection={defaultRosePlotDirection}
        pointProps={props.pointProps}
        pointName={props.pointName}
        point={point}
        userInput={userInput}
        updateUserInput={updateUserInput}
        errorRosePlot={rosePlot.errorRosePlot}
        loadingRosePlot={rosePlot.loadingRosePlot}
        handleChangePoint={commonInputAnalytics.handleChangePoint}
        pointOptions={commonInputAnalytics.pointOptions}
        updateRosePlotData={rosePlot.updateRosePlotData}
        setUpdatedRosePlotData={rosePlot.setUpdatedRosePlotData}
        onUpdateRosePlot={rosePlot.onUpdateRosePlot}
        isOffline={isOfflineDataset}
      />
      <ScatterPlotBox
        enrichedMetadata={enrichedMetadata}
        coordinates={props.coordinates}
        previewDatasetId={props.previewDatasetId}
        previewDatasetTimeRange={getDateRange(props.previewDatasetId, props.userGroupPermissions, isTnw, isDwf)}
        isForAllAnalytics={isForAllAnalytics}
        setIsForAllAnalytics={setIsForAllAnalytics}
        globalDateRange={analyticsDateTime}
        setGlobalDateRange={setAnalyticsDateTime}
        categoryDataset={commonInputAnalytics.categoryDataset}
        onDatasetChange={commonInputAnalytics.onDatasetChange}
        onUpdateScatterPlot={scatterPlot.onUpdateScatterPlot}
        updatedScatterPlotData={scatterPlot.updatedScatterPlotData}
        onUpdateJointScatterPlot={scatterPlot.onUpdateJointScatterPlot}
        pointProps={props.pointProps}
        pointName={props.pointName}
        userInput={userInput}
        updateUserInput={updateUserInput}
        gridCellSize={props.gridCellSize}
        waterDepth={props.waterDepth}
        activePlotAnalytics={analyticsNavigation.activePlotAnalytics[0]}
        handleChangePoint={commonInputAnalytics.handleChangePoint}
        errorScatterPlot={scatterPlot.errorScatterPlot}
        loadingScatterPlot={scatterPlot.loadingScatterPlot}
        pointOptions={commonInputAnalytics.pointOptions}
        scatterPlotPreview={timeSeriesPreview}
        scatterPlotPreviewError={timeSeriesPreviewError}
        isOffline={isOfflineDataset}
      />
      <ScatterTableBox
        previewDatasetId={props.previewDatasetId}
        coordinates={props.coordinates}
        categoryDataset={commonInputAnalytics.categoryDataset}
        isForAllAnalytics={isForAllAnalytics}
        setIsForAllAnalytics={setIsForAllAnalytics}
        globalDateRange={analyticsDateTime}
        setGlobalDateRange={setAnalyticsDateTime}
        dateRange={getDateRange(props.previewDatasetId, props.userGroupPermissions, isTnw, isDwf)}
        onDatasetChange={commonInputAnalytics.onDatasetChange}
        pointName={props.pointName}
        enrichedMetadata={enrichedMetadata}
        userInput={userInput}
        updateUserInput={updateUserInput}
        gridCellSize={props.gridCellSize}
        waterDepth={props.waterDepth}
        onChangeActiveKey={commonInputAnalytics.handleChangePoint}
        pointOption={commonInputAnalytics.pointOptions}
        point={point}
        scatterTableData={scatterTable.scatterTableData}
        scatterTableLinks={scatterTable.scatterTableLinks}
        scatterTableError={scatterTable.scatterTableError}
        updatingScatterTable={scatterTable.isUpdatingScatterTable}
        onUpdateScatterTable={scatterTable.updateScatterTable}
        activePlotAnalytics={analyticsNavigation.activePlotAnalytics[0]}
        isOfflineDataset={isOfflineDataset}
      />
      <PersistenceBox
        enrichedMetadata={enrichedMetadata}
        activePlotAnalytics={analyticsNavigation.activePlotAnalytics[0]}
        isUpdating={advancedAnalytics.isUpdating}
        previewDatasetId={props.previewDatasetId}
        coordinates={props.coordinates}
        categoryDataset={commonInputAnalytics.categoryDataset}
        dateRange={getDateRange(props.previewDatasetId, props.userGroupPermissions, isTnw, isDwf)}
        onUpdate={advancedAnalytics.updatePersistence}
        persistenceImage={advancedAnalytics.images.persistence}
        error={advancedAnalytics.errors.persistence}
        persistenceLinks={advancedAnalytics.links.persistence}
        pointName={props.pointName}
        userInput={userInput}
        updateUserInput={updateUserInput}
        onDatasetChange={commonInputAnalytics.onDatasetChange}
        gridCellSize={props.gridCellSize}
        waterDepth={props.waterDepth}
        point={point}
        onChangeActiveKey={commonInputAnalytics.handleChangePoint}
        pointOption={commonInputAnalytics.pointOptions}
        isForAllAnalytics={isForAllAnalytics}
        setIsForAllAnalytics={setIsForAllAnalytics}
        globalDateRange={analyticsDateTime}
        setGlobalDateRange={setAnalyticsDateTime}
        isOfflineDataset={isOfflineDataset}
      />
      <ExtremeValuesBox
        activePlotAnalytics={analyticsNavigation.activePlotAnalytics[0]}
        previewDatasetId={props.previewDatasetId}
        coordinates={props.coordinates}
        datasets={commonInputAnalytics.categoryDataset}
        dateRange={getDateRange(props.previewDatasetId, props.userGroupPermissions, isTnw, isDwf)}
        enrichedMetadata={enrichedMetadata}
        pointName={props.pointName}
        userInput={userInput}
        updateUserInput={updateUserInput}
        onDatasetChange={commonInputAnalytics.onDatasetChange}
        isForAllAnalytics={isForAllAnalytics}
        setIsForAllAnalytics={setIsForAllAnalytics}
        globalDateRange={analyticsDateTime}
        setGlobalDateRange={setAnalyticsDateTime}
        onUpdate={advancedAnalytics.updateExtremeValues}
        extremeValuesImage={advancedAnalytics.images.extremeValues}
        extremeValuesLinks={advancedAnalytics.links.extremeValues}
        error={advancedAnalytics.errors.extremeValues}
        updating={advancedAnalytics.isUpdating}
        isOfflineDataset={isOfflineDataset}
      />
      <HistogramBox
        activePlotAnalytics={analyticsNavigation.activePlotAnalytics[0]}
        previewDatasetId={props.previewDatasetId}
        coordinates={props.coordinates}
        datasets={commonInputAnalytics.categoryDataset}
        dateRange={getDateRange(props.previewDatasetId, props.userGroupPermissions, isTnw, isDwf)}
        enrichedMetadata={enrichedMetadata}
        pointName={props.pointName}
        userInput={userInput}
        updateUserInput={updateUserInput}
        point={point}
        onDatasetChange={commonInputAnalytics.onDatasetChange}
        onChangeActiveKey={commonInputAnalytics.handleChangePoint}
        pointOption={commonInputAnalytics.pointOptions}
        isForAllAnalytics={isForAllAnalytics}
        setIsForAllAnalytics={setIsForAllAnalytics}
        globalDateRange={analyticsDateTime}
        setGlobalDateRange={setAnalyticsDateTime}
        onUpdate={advancedAnalytics.updateHistogram}
        histogramImage={advancedAnalytics.images.histogram}
        histogramLinks={advancedAnalytics.links.histogram}
        error={advancedAnalytics.errors.histogram}
        updating={advancedAnalytics.isUpdating}
        isOfflineDataset={isOfflineDataset}
      />
      <TidalLevelBox
        activePlotAnalytics={analyticsNavigation.activePlotAnalytics[0]}
        previewDatasetId={props.previewDatasetId}
        coordinates={props.coordinates}
        datasets={commonInputAnalytics.categoryDataset}
        dateRange={getDateRange(props.previewDatasetId, props.userGroupPermissions, isTnw, isDwf)}
        enrichedMetadata={enrichedMetadata}
        updating={advancedAnalytics.isUpdating}
        onUpdate={advancedAnalytics.updateTidalLevel}
        tidalLevelValueImage={advancedAnalytics.images.tidal}
        tidalValueLinks={advancedAnalytics.links.tidal}
        error={advancedAnalytics.errors.tidal}
        pointName={props.pointName}
        userInput={userInput}
        updateUserInput={updateUserInput}
        onDatasetChange={commonInputAnalytics.onDatasetChange}
        point={point} onChangeActiveKey={commonInputAnalytics.handleChangePoint}
        pointOption={commonInputAnalytics.pointOptions}
        isForAllAnalytics={isForAllAnalytics}
        setIsForAllAnalytics={setIsForAllAnalytics}
        globalDateRange={analyticsDateTime}
        setGlobalDateRange={setAnalyticsDateTime}
        isOfflineDataset={isOfflineDataset}
      />
      <StatisticsBox
        activePlotAnalytics={analyticsNavigation.activePlotAnalytics[0]}
        previewDatasetId={props.previewDatasetId}
        coordinates={props.coordinates}
        datasets={commonInputAnalytics.categoryDataset}
        onDatasetChange={commonInputAnalytics.onDatasetChange}
        dateRange={getDateRange(props.previewDatasetId, props.userGroupPermissions, true, isDwf)}
        enrichedMetadata={enrichedMetadata}
        pointName={props.pointName}
        userInput={userInput}
        updateUserInput={updateUserInput}
        point={point}
        onChangeActiveKey={commonInputAnalytics.handleChangePoint}
        pointOption={commonInputAnalytics.pointOptions}
        isForAllAnalytics={isForAllAnalytics}
        setIsForAllAnalytics={setIsForAllAnalytics}
        globalDateRange={analyticsDateTime}
        setGlobalDateRange={setAnalyticsDateTime}
        onUpdate={advancedAnalytics.updateStatistics}
        statisticsImages={advancedAnalytics.images.statistics}
        statisticsValuesLinks={advancedAnalytics.links.statistics}
        error={advancedAnalytics.errors.statistics}
        updating={advancedAnalytics.isUpdating}
        isOfflineDataset={isOfflineDataset}
      />
      <ExceedanceBox
        activePlotAnalytics={analyticsNavigation.activePlotAnalytics[0]}
        previewDatasetId={props.previewDatasetId}
        coordinates={props.coordinates}
        datasets={commonInputAnalytics.categoryDataset}
        dateRange={getDateRange(props.previewDatasetId, props.userGroupPermissions, true, isDwf)}
        enrichedMetadata={enrichedMetadata}
        updating={advancedAnalytics.isUpdating}
        onUpdate={advancedAnalytics.updateExceedance}
        exceedanceValuesImage={advancedAnalytics.images.exceedance}
        exceedanceValuesLinks={advancedAnalytics.links.exceedance}
        error={advancedAnalytics.errors.exceedance}
        pointName={props.pointName}
        userInput={userInput}
        updateUserInput={updateUserInput}
        onDatasetChange={commonInputAnalytics.onDatasetChange}
        point={point}
        onChangeActiveKey={commonInputAnalytics.handleChangePoint}
        pointOption={commonInputAnalytics.pointOptions}
        isForAllAnalytics={isForAllAnalytics}
        setIsForAllAnalytics={setIsForAllAnalytics}
        globalDateRange={analyticsDateTime}
        setGlobalDateRange={setAnalyticsDateTime}
        isOfflineDataset={isOfflineDataset}
      />
    </AnalyticsLayout>
    </>
  )
}
